import { useEffect, useRef, useState } from "react";
import { socket, type StreamReadyData } from "../socket";
import { CodeBlock, Text } from "@mittwald/flow-remote-react-components";
import { CustomLoadingSpinner } from "@wiesinghilker/extension-lib-frontend";

export const ErrorLog = (props: {
  appInstallationId: string;
  projectId: string;
}) => {
  const { appInstallationId } = props;
  const [isReady, setIsReady] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  /** Last line received, so a resumed stream only replays what was missed. */
  const lastSeqRef = useRef(0);
  /** The stream that `lastSeqRef` belongs to; sequences restart per stream. */
  const streamIdRef = useRef<string | undefined>(undefined);

  // Another app installation means another stream — nothing carries over. The
  // parent keys this component by appInstallationId, so a fresh instance (and
  // fresh state) is created instead of resetting it here.
  useEffect(() => {
    const onConnect = () => {
      setIsConnected(true);
      socket.emit("subscribe", {
        appInstallationId,
        lastSeq: lastSeqRef.current,
        streamId: streamIdRef.current,
      });
    };

    // The websocket dropped. The tail keeps running on the server, so this is
    // a temporary interruption — socket.io reconnects and resumes it.
    const onDisconnect = () => {
      setIsConnected(false);
    };

    const onSSHConnected = (data?: StreamReadyData) => {
      if (data?.streamId !== streamIdRef.current) {
        streamIdRef.current = data?.streamId;
        lastSeqRef.current = 0;
      }

      setIsReady(true);
      setErrorMessage(null);
    };

    const onLog = (line: string, seq?: number) => {
      if (seq !== undefined) {
        lastSeqRef.current = seq;
      }
      setLogs((prev) => [line, ...prev.slice(0, 99)]);
    };

    const onLogStreamClosed = () => {
      setIsReady(false);
      // A preceding error event carries the actual reason — keep it.
      setErrorMessage((prev) => prev ?? "Der Error-Log-Stream wurde beendet.");
    };

    const onError = (message: string) => {
      setErrorMessage(message);
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("sshConnected", onSSHConnected);
    socket.on("log", onLog);
    socket.on("logStreamClosed", onLogStreamClosed);
    socket.on("error", onError);

    socket.connect();

    return () => {
      // Explicit teardown, unlike a transport drop: the server disposes the
      // SSH connection right away instead of holding it for a reconnect.
      socket.emit("unsubscribe");
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("sshConnected", onSSHConnected);
      socket.off("log", onLog);
      socket.off("logStreamClosed", onLogStreamClosed);
      socket.off("error", onError);
      socket.disconnect();
    };
  }, [appInstallationId]);

  if (!isReady && !errorMessage) {
    return <CustomLoadingSpinner text="Verbinde mit Server" />;
  }

  return (
    <>
      {errorMessage ? (
        <Text>{errorMessage}</Text>
      ) : isConnected ? (
        <CustomLoadingSpinner text="Empfange Live Error-Log" />
      ) : (
        <CustomLoadingSpinner text="Verbindung wird wiederhergestellt..." />
      )}
      <CodeBlock
        code={`...${logs.length == 0 ? "\n" : "\n\n"}${logs.join("\n")}`}
      />
    </>
  );
};
